import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  usePickupTimeRanges, PickupTimeRangeSetting, pickupTimeRangeOptions,
} from "@/components/PickupTimeRangeSetting";
import { Plus, ChevronLeft, ChevronRight, Edit, X } from "lucide-react";

// "Picked *" — Edgistify's marker for a historical pickup whose Delhivery record
// no longer exists, so it couldn't be confirmed as picked. Set only by Edgistify
// admins; mirrors UNVERIFIED_PICKED_STATUS in the backend.
const UNVERIFIED_PICKED = "Picked *";
const UNVERIFIED_PICKED_TOOLTIP =
  "Picked * — unverified. Delhivery's historical pickup record is no longer available, so this pickup could not be confirmed as picked.";

const STATUS_COLORS = {
  "Created": "bg-slate-100 text-slate-800",
  "Scheduled": "bg-blue-100 text-blue-800",
  "Out For Pickup": "bg-yellow-100 text-yellow-800",
  "Picked": "bg-green-100 text-green-800",
  "Not picked": "bg-red-100 text-red-800",
  "Cancelled": "bg-gray-100 text-gray-800",
  // Grey + dashed border: deliberately unlike the green confirmed "Picked".
  [UNVERIFIED_PICKED]: "border border-dashed border-slate-400 bg-slate-50 text-slate-600 hover:bg-slate-50",
};


const STATUSES = ["Created", "Scheduled", "Out For Pickup", "Picked", "Not picked", "Cancelled", UNVERIFIED_PICKED];

// Statuses with no seller Edit / Cancel (the backend refuses them too).
const FINAL_FOR_SELLER = ["Out For Pickup", "Picked", "Not picked", "Cancelled", UNVERIFIED_PICKED];

const ITEMS_PER_PAGE = 50;

const SellerPickupRequests = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  // Admin-configured pickup slots, shared with the other Pickup Requests page.
  const { config: trConfig, saving: trSaving, save: saveTimeRanges } = usePickupTimeRanges();
  const noTimeRanges = (trConfig.options || []).length === 0;
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    pickup_location_id: "",
    pickup_date: "",
    time_range: "",
    expected_package_count: "",
    notes: ""
  });
  
  const [pickupLocations, setPickupLocations] = useState([]);
  // Wallet gate — same rule as seller order creation (CreateOrder.jsx).
  const [sellerWalletInfo, setSellerWalletInfo] = useState(null);
  const [isSellerLowBalance, setIsSellerLowBalance] = useState(false);

  useEffect(() => {
    fetchPickupLocations();
    checkWalletBalance();
  }, []);

  const checkWalletBalance = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet/balance`);
      const enforce = data.enforce_wallet_minimum_for_orders !== false;
      setSellerWalletInfo(data);
      setIsSellerLowBalance(enforce && data.balance <= data.min_wallet_balance);
    } catch { /* non-fatal: the backend still enforces the same rule on submit */ }
  };

  useEffect(() => {
    fetchRequests();
  }, [currentPage, searchQuery, selectedStatus, startDate, endDate]);

  const fetchPickupLocations = async () => {
    try {
      const response = await axios.get(`${API}/pickup-locations/`);
      setPickupLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch pickup locations");
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });
      
      if (searchQuery.trim()) params.append('search', searchQuery);
      if (selectedStatus && selectedStatus !== "all") params.append('status', selectedStatus);
      if (startDate) params.append('date_from', startDate);
      if (endDate) params.append('date_to', endDate);
      
      const response = await axios.get(`${API}/pickup-requests/?${params.toString()}`);
      setRequests(response.data.requests);
      setTotalPages(response.data.pages);
      setTotalRequests(response.data.total);
    } catch (error) {
      toast.error("Failed to fetch pickup requests");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      pickup_location_id: "",
      pickup_date: "",
      time_range: "",
      expected_package_count: "",
      notes: ""
    });
    setShowCreateDialog(true);
  };

  const handleCreateRequest = async () => {
    if (!formData.pickup_location_id || !formData.pickup_date ||
        !formData.time_range || !formData.expected_package_count) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (isSellerLowBalance) {
      toast.error("Your wallet balance is below the minimum. Pickup request creation is not allowed. Please recharge your wallet.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/pickup-requests/`, {
        seller_id: user.seller_id,
        pickup_location_id: formData.pickup_location_id,
        pickup_date: formData.pickup_date,
        time_range: formData.time_range,
        expected_package_count: parseInt(formData.expected_package_count),
        notes: formData.notes || null
      });
      
      toast.success("Pickup request created successfully");
      setShowCreateDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create pickup request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditDialog = (request) => {
    setSelectedRequest(request);
    setFormData({
      pickup_location_id: request.pickup_location_id,
      pickup_date: request.pickup_date,
      time_range: request.time_range,
      expected_package_count: request.expected_package_count.toString(),
      notes: request.notes || ""
    });
    setShowEditDialog(true);
  };

  const handleUpdateRequest = async () => {
    if (!formData.pickup_date || !formData.time_range || !formData.expected_package_count) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/pickup-requests/${selectedRequest.id}`, {
        pickup_location_id: formData.pickup_location_id,
        pickup_date: formData.pickup_date,
        time_range: formData.time_range,
        expected_package_count: parseInt(formData.expected_package_count),
        notes: formData.notes || null
      });
      
      toast.success("Pickup request updated successfully");
      setShowEditDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update pickup request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (request) => {
    const confirmed = window.confirm(`Are you sure you want to cancel pickup request ${request.request_id}?`);
    if (!confirmed) return;

    try {
      await axios.patch(`${API}/pickup-requests/${request.id}/cancel`);
      toast.success("Pickup request cancelled");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    }
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Pickup Requests</h1>
            <p className="text-slate-600 mt-1">Manage your pickup requests</p>
          </div>
          <div className="flex items-end gap-4">
            <PickupTimeRangeSetting config={trConfig} saving={trSaving} onSave={saveTimeRanges} />
            <Button onClick={handleOpenCreateDialog} disabled={noTimeRanges}
              title={noTimeRanges ? "No pickup time ranges are currently available" : undefined}>
              <Plus className="w-4 h-4 mr-2" />
              Create Request
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm">Search</Label>
                <SearchInput
                  onSearch={setSearchQuery}
                  placeholder="Search by Request ID..."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm">Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm">From Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-sm">To Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-end">
                <Button variant="outline" onClick={handleClearFilters} className="w-full">
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-600">Loading...</div>
            ) : requests.length === 0 ? (
              <div className="p-8 text-center text-slate-600">No pickup requests found</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Request ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Pickup Location</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Pickup Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Time Range</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Package Count</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {requests.map((request) => (
                        <tr key={request.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle text-sm">
                            <span className="font-medium text-teal-600">{request.request_id}</span>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div>{request.pickup_location_name}</div>
                            <div className="text-xs text-slate-500">{request.pickup_location_city}</div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">{request.pickup_date}</td>
                          <td className="px-4 py-3 align-middle text-sm whitespace-nowrap">{request.time_range}</td>
                          <td className="px-4 py-3 align-middle text-sm text-right tabular-nums">{request.expected_package_count}</td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <Badge
                              className={STATUS_COLORS[request.status] || "bg-gray-100 text-gray-800"}
                              title={request.status === UNVERIFIED_PICKED ? UNVERIFIED_PICKED_TOOLTIP : undefined}
                              aria-label={request.status === UNVERIFIED_PICKED ? UNVERIFIED_PICKED_TOOLTIP : undefined}
                            >
                              {request.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex gap-2">
                              {!FINAL_FOR_SELLER.includes(request.status) && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenEditDialog(request)}
                                    title="Edit Request"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCancelRequest(request)}
                                    title="Cancel Request"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-600">
                    Showing {requests.length} of {totalRequests} requests
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Pickup Request</DialogTitle>
            <DialogDescription>Schedule a new pickup request</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {isSellerLowBalance && sellerWalletInfo && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 font-semibold text-sm">Low Balance Alert</p>
                <p className="text-sm text-red-700 mt-1">
                  Your wallet balance (₹{sellerWalletInfo.balance?.toLocaleString()}) is at or below the minimum
                  threshold (₹{sellerWalletInfo.min_wallet_balance?.toLocaleString()}). Pickup request creation is not
                  allowed. Please recharge your wallet.
                </p>
              </div>
            )}
            <div>
              <Label>Pickup Location *</Label>
              <Select 
                value={formData.pickup_location_id} 
                onValueChange={(val) => setFormData({...formData, pickup_location_id: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select pickup location" />
                </SelectTrigger>
                <SelectContent>
                  {pickupLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.facility_name} - {loc.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Pickup Date *</Label>
              <Input
                type="date"
                value={formData.pickup_date}
                onChange={(e) => setFormData({...formData, pickup_date: e.target.value})}
                className="mt-1"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label>Time Range *</Label>
              <Select 
                value={formData.time_range} 
                onValueChange={(val) => setFormData({...formData, time_range: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  {pickupTimeRangeOptions(trConfig, formData.time_range).map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Expected Package Count *</Label>
              <Input
                type="number"
                min="1"
                value={formData.expected_package_count}
                onChange={(e) => setFormData({...formData, expected_package_count: e.target.value})}
                className="mt-1"
                placeholder="Enter package count"
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1"
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRequest} disabled={submitting || isSellerLowBalance}>
              {submitting ? "Creating..." : "Create Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Pickup Request</DialogTitle>
            <DialogDescription>Update pickup request details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Pickup Location *</Label>
              <Select 
                value={formData.pickup_location_id} 
                onValueChange={(val) => setFormData({...formData, pickup_location_id: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select pickup location" />
                </SelectTrigger>
                <SelectContent>
                  {pickupLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.facility_name} - {loc.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Pickup Date *</Label>
              <Input
                type="date"
                value={formData.pickup_date}
                onChange={(e) => setFormData({...formData, pickup_date: e.target.value})}
                className="mt-1"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label>Time Range *</Label>
              <Select 
                value={formData.time_range} 
                onValueChange={(val) => setFormData({...formData, time_range: val})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  {pickupTimeRangeOptions(trConfig, formData.time_range).map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Expected Package Count *</Label>
              <Input
                type="number"
                min="1"
                value={formData.expected_package_count}
                onChange={(e) => setFormData({...formData, expected_package_count: e.target.value})}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateRequest} disabled={submitting}>
              {submitting ? "Updating..." : "Update Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default SellerPickupRequests;
